import { useEffect, useState, useCallback } from 'react'
import { Card } from '@/components/ui/Card'
import { KpiCard } from '@/components/ui/KpiCard'
import { Tabs, type Tab } from '@/components/ui/Tabs'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import {
  getDashboardStats,
  getDashboardWorkers,
  getDashboardRedis,
  type DashboardStats,
  type DashboardWorkers,
  type DashboardRedis,
} from '@/api/dashboard'
import { getSessions, type SessionSummary } from '@/api/interview'
import { SessionsTable } from '@/components/dashboard/SessionsTable'
import { WorkersTable } from '@/components/dashboard/WorkersTable'

export function DashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [workers, setWorkers] = useState<DashboardWorkers | null>(null)
  const [redis, setRedis] = useState<DashboardRedis | null>(null)
  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [sessionsTotal, setSessionsTotal] = useState(0)
  const [sessionsPage, setSessionsPage] = useState(0)
  const sessionsPerPage = 10
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('sessions')
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const fetchDashboardData = useCallback(async (sessionSkip = 0) => {
    try {
      const [statsRes, workersRes, redisRes, sessionsRes] = await Promise.all([
        getDashboardStats(),
        getDashboardWorkers(),
        getDashboardRedis(),
        getSessions('', ['-created_at'], sessionSkip, sessionsPerPage),
      ])
      setStats(statsRes)
      setWorkers(workersRes)
      setRedis(redisRes)
      setSessions(sessionsRes.items)
      setSessionsTotal(sessionsRes.total)
      setLastUpdated(new Date())
    } catch {
      // silently fail on refresh — stale data is still visible
    }
  }, [sessionsPerPage])

  useEffect(() => {
    async function init() {
      await fetchDashboardData(0)
      setLoading(false)
    }
    init()

    const interval = setInterval(() => fetchDashboardData(sessionsPage * sessionsPerPage), 10_000)
    return () => clearInterval(interval)
  }, [fetchDashboardData, sessionsPage, sessionsPerPage])

  function handleSessionsPageChange(newPage: number) {
    setSessionsPage(newPage)
    fetchDashboardData(newPage * sessionsPerPage)
  }

  if (loading) {
    return (
      <div className="container" style={{ display: 'flex', justifyContent: 'center', paddingTop: '6rem' }}>
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const w = workers?.stats
  const workerList = workers?.workers ?? []

  const tabs: Tab[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'sessions', label: 'Sessions' },
    { key: 'workers', label: 'Workers', count: workerList.length },
  ]


  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%", alignItems: "center" }}>
      {/* Header */}
      <Card className="dashboard-header w-full">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Dashboard</h1>
          {lastUpdated && (
            <span style={{ fontSize: '0.8rem', opacity: 0.5 }}>
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </Card>
      <div className="container" style={{ maxWidth: 1100 }}>

        {/* KPI Grid — Workers */}
        {w && (
          <div className="kpi-grid">
            <KpiCard value={w.total_workers} label="Total Workers" />
            <KpiCard value={w.de_active} label="Active DE" />
            <KpiCard value={w.en_active} label="Active EN" />
            <KpiCard value={w.initializing_count} label="Initializing" />
            <KpiCard value={w.de_ready_count} label="DE Ready Pool" />
            <KpiCard value={w.en_ready_count} label="EN Ready Pool" />
          </div>
        )}

        {/* KPI Grid — Redis */}
        {redis?.summary && (
          <div className="kpi-grid">
            <KpiCard value={redis.summary.warm_workers} label="Warm Workers" />
            <KpiCard value={redis.summary.active_workers} label="Active Workers" />
            <KpiCard value={redis.summary.ready_workers} label="Ready Workers" />
            <KpiCard value={redis.summary.orphaned_worker_keys} label="Orphaned Keys" />
            <KpiCard value={redis.summary.session_mappings} label="Session Mappings" />
            <KpiCard value={redis.summary.active_sessions} label="Active Sessions" />
          </div>
        )}

        {/* Tabs */}
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

        {/* Tab Content */}
        <div style={{ marginTop: '1rem' }}>
          {activeTab === 'overview' && w && (
            <Card>
              <h3 style={{ margin: '0 0 1rem' }}>Worker Pool Status</h3>
              <div className="overview-list">
                <div className="overview-item">
                  <span className="overview-label">Total Workers</span>
                  <span className="overview-value">{w.total_workers}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">Active Sessions</span>
                  <span className="overview-value">{w.active_sessions_count}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">DE Active / Ready</span>
                  <span className="overview-value">{w.de_active} / {w.de_ready_count}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">EN Active / Ready</span>
                  <span className="overview-value">{w.en_active} / {w.en_ready_count}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">Initializing</span>
                  <span className="overview-value">{w.initializing_count}</span>
                </div>
                {stats?.last_24h && (
                  <div className="overview-item">
                    <span className="overview-label">Success Rate (24h)</span>
                    <span className="overview-value">{(stats.last_24h.success_rate * 100).toFixed(1)}%</span>
                  </div>
                )}
              </div>
            </Card>
          )}

          {activeTab === 'workers' && <WorkersTable workers={workerList} />}

          {activeTab === 'sessions' && (
            <SessionsTable
              sessions={sessions}
              total={sessionsTotal}
              page={sessionsPage}
              pageSize={sessionsPerPage}
              onPageChange={handleSessionsPageChange}
            />
          )}
        </div>
      </div>
    </div>
  )
}
